import { expireSessionAndRedirectToLogin } from '@/features/session/services/session-expiration'
import { getRuntimeConfig } from './runtime-config'

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

/** Avoid treating third-party 401s as a ForgeSphere session failure. */
export function isForgeSphereApiUrl(
  input: RequestInfo | URL,
  baseURL?: string,
): boolean {
  try {
    const baseOrigin =
      typeof window === 'undefined'
        ? 'https://forgesphere.local'
        : window.location.origin
    const requestBase = new URL(baseURL || baseOrigin, baseOrigin)
    const rawUrl = requestUrl(input)
    const target = new URL(rawUrl, requestBase)
    const absoluteRequest = /^[a-z][a-z\d+.-]*:\/\//i.test(rawUrl)
    if (target.hostname === 'forgesphere.probestack.io') return true

    // Legacy migration services have their own local ports and are not in config.json.
    if (
      /^(localhost|127\.0\.0\.1)$/.test(target.hostname) &&
      ['8088', '8089', '8090', '8091', '8094'].includes(target.port) &&
      /^\/(configuration|discovery|assessment|enhancement|apigee-migration)(\/|$)/.test(
        target.pathname,
      )
    ) {
      return true
    }

    const services = getRuntimeConfig().services
    return Object.values(services).some((serviceUrl) => {
      if (!serviceUrl) return false
      const service = new URL(serviceUrl, baseOrigin)
      const path = service.pathname.replace(/\/$/, '')
      if (baseURL && !absoluteRequest) {
        if (
          requestBase.origin === service.origin &&
          requestBase.pathname.replace(/\/$/, '') === path
        ) {
          return true
        }
      }
      return (
        target.origin === service.origin &&
        (target.pathname === path || target.pathname.startsWith(path + '/'))
      )
    })
  } catch {
    return false
  }
}

/** Fetch for configured ForgeSphere APIs, including SSE endpoints. */
export async function forgeSphereFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init)
  if (response.status === 401 && isForgeSphereApiUrl(input)) {
    expireSessionAndRedirectToLogin()
  }
  return response
}
